import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  BulkUpdateGHADiscountRulesParams,
  BulkUpdateGHADiscountRulesResponse,
  CreateGHADiscountRuleBody,
  GenerateGHADiscountTokenBody,
  ListGHADiscountRulesFilters,
  archiveGHADiscountRule,
  bulkUpdateGHADiscountRules,
  createGHADiscountRule,
  generateGHADiscountTokenDebug,
  listGHADiscountRules,
} from '~/services/ConnectionGoogleService';

export const ghaDiscountRuleQueryKeys = {
  all: ['ghaDiscountRules'] as const,
  lists: () => [...ghaDiscountRuleQueryKeys.all, 'list'] as const,
  list: (filters: ListGHADiscountRulesFilters) => [...ghaDiscountRuleQueryKeys.lists(), filters] as const,
};

export function useGetGHADiscountRules(filters: ListGHADiscountRulesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ghaDiscountRuleQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHADiscountRules(filters, { signal });
      return { result: response.result, total: response.total };
    },
    enabled: options.enabled ?? true,
  });
}

export function useCreateGHADiscountRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: CreateGHADiscountRuleBody) => {
      return await createGHADiscountRule(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar('GHA discount rule created', { variant: 'success' });
    },
    // Errors are surfaced inline on the create dialog (the backend returns a
    // generic 500 for duplicates), so no snackbar here.
  });
}

export function useGenerateGHADiscountToken() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: GenerateGHADiscountTokenBody) => {
      return await generateGHADiscountTokenDebug(input);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to generate token: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useArchiveGHADiscountRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      return await archiveGHADiscountRule(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar('GHA discount rule archived', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to archive GHA discount rule: ${error.message}`, { variant: 'error' });
    },
  });
}

function formatBulkDiscountRulesSummary({ result }: BulkUpdateGHADiscountRulesResponse) {
  return `Bulk discounts applied - ${result.created} created, ${result.replaced} replaced, ${result.removed} removed, ${result.unchanged} unchanged, ${result.skipped} skipped`;
}

export function useBulkUpdateGHADiscountRules() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: BulkUpdateGHADiscountRulesParams) => {
      return await bulkUpdateGHADiscountRules(input);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ghaDiscountRuleQueryKeys.lists() });
      enqueueSnackbar(formatBulkDiscountRulesSummary(response), { variant: 'success' });
    },
  });
}
